import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { signIn, signOut, confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Card, CardHeader, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/lib/routes';

type LoginStep = 'credentials' | 'new-password' | 'totp' | 'forgot-password' | 'reset-code';

// Amplify keeps tokens in localStorage. When they go stale (refresh token
// expired, password reset, sessions revoked) the app treats the user as
// signed out, but signIn() still sees the old tokens and throws
// UserAlreadyAuthenticatedException. Clear the local session and retry once.
async function signInClearingStaleSession(username: string, password: string) {
  try {
    return await signIn({ username, password });
  } catch (err) {
    if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
      await signOut().catch(() => {});
      return signIn({ username, password });
    }
    throw err;
  }
}

export function LoginPage() {
  const navigate = useNavigate();
  const { refreshAuth, isAuthenticated, isLoading } = useAuth();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A valid session landing on /login (bookmark, back button) goes straight in
  if (!isLoading && isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await signInClearingStaleSession(email, password);

      if (result.isSignedIn) {
        await refreshAuth();
        navigate(ROUTES.DASHBOARD, { replace: true });
        return;
      }

      switch (result.nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          setStep('new-password');
          break;
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          setStep('totp');
          break;
        default:
          setError(`Unsupported sign-in step: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });

      if (result.isSignedIn) {
        await refreshAuth();
        navigate(ROUTES.DASHBOARD, { replace: true });
        return;
      }

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp');
      } else {
        setError(`Unsupported step: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await confirmSignIn({ challengeResponse: totpCode });

      if (result.isSignedIn) {
        await refreshAuth();
        navigate(ROUTES.DASHBOARD, { replace: true });
      } else {
        setError(`Unsupported step: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await resetPassword({ username: resetEmail });
      setStep('reset-code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResetCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword({
        username: resetEmail,
        confirmationCode: resetCode,
        newPassword: resetNewPassword,
      });
      // Reset form and go back to credentials
      setSuccessMessage('Password reset successfully. You can now sign in with your new password.');
      setStep('credentials');
      setResetEmail('');
      setResetCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(0,0%,4%)] px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          {/* Logo — already contains "Integration Studio" text */}
          <div className="mx-auto mb-2">
            <img src="/logo.png" alt="Integration Studio" className="h-24 w-auto mx-auto" />
          </div>
          <CardDescription className="text-xs uppercase tracking-wider">
            {step === 'credentials' && '// Authentication_Required'}
            {step === 'new-password' && '// Set_New_Password'}
            {step === 'totp' && '// Verification_Required'}
            {step === 'forgot-password' && '// Password_Recovery'}
            {step === 'reset-code' && '// Reset_Password'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {successMessage && (
            <div className="mb-4 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          {step === 'credentials' && (
            <form onSubmit={handleCredentials} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Authenticating...' : 'Sign_In'}
              </Button>
              <div className="mt-3 text-center">
                <button
                  type="button"
                  className="text-[11px] text-gray-500 hover:text-emerald-400 transition-colors"
                  onClick={() => {
                    setStep('forgot-password');
                    setError('');
                    setSuccessMessage('');
                    setResetEmail(email);
                  }}
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {step === 'new-password' && (
            <form onSubmit={handleNewPassword} className="space-y-4">
              <p className="text-xs text-gray-500">
                Your administrator created your account. Please choose a new password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-[10px] uppercase tracking-wider text-gray-500">
                  New_Password
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Confirm_Password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <p className="text-[10px] text-gray-600">
                Minimum 12 characters, with uppercase, lowercase, numbers, and symbols.
              </p>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Processing...' : 'Set_Password'}
              </Button>
            </form>
          )}

          {step === 'totp' && (
            <form onSubmit={handleTotp} className="space-y-4">
              <p className="text-xs text-gray-500">
                Enter the 6-digit code from your authenticator app.
              </p>
              <div className="space-y-2">
                <Label htmlFor="totp-code" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Verification_Code
                </Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  required
                  autoFocus
                  className="text-center tracking-[0.5em]"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying...' : 'Verify'}
              </Button>
            </form>
          )}

          {step === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-xs text-gray-500">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Email
                </Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="admin@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send_Reset_Code'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="text-[11px] text-gray-500 hover:text-emerald-400 transition-colors"
                  onClick={() => {
                    setStep('credentials');
                    setError('');
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}

          {step === 'reset-code' && (
            <form onSubmit={handleResetCode} className="space-y-4">
              <p className="text-xs text-gray-500">
                Enter the verification code sent to <span className="text-emerald-400">{resetEmail}</span> and choose a new password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="verification-code" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Verification_Code
                </Label>
                <Input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  autoFocus
                  className="text-center tracking-[0.3em]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-new-password" className="text-[10px] uppercase tracking-wider text-gray-500">
                  New_Password
                </Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-confirm-password" className="text-[10px] uppercase tracking-wider text-gray-500">
                  Confirm_Password
                </Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <p className="text-[10px] text-gray-600">
                Minimum 12 characters, with uppercase, lowercase, numbers, and symbols.
              </p>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Resetting...' : 'Reset_Password'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="text-[11px] text-gray-500 hover:text-emerald-400 transition-colors"
                  onClick={() => {
                    setStep('forgot-password');
                    setError('');
                  }}
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="fixed bottom-4 left-0 right-0 text-center">
        <p className="text-[10px] uppercase tracking-wider text-gray-600">
          // System_Version: v3.2.1
        </p>
      </div>
    </div>
  );
}
